/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drug shipment received modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, makeStyles, Theme, Typography, Box, DialogContentText } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import NumberTextField from '../../../components/FinalForm/FinalFormControls/NumberTextField';
import { getDrugUnitName } from '../../../utilities/getDrugUnitName';
import useAsyncFunction from '../../../hooks/useAsyncFunction';
import { DrugShipmentVerifyActions } from '../../../store/modules/drugmanagement/drugshipmentverify';
import drugManagementSettings from '../../../constants/drugManagementSettings';
import DatePickerWrapper from '../../../components/FinalForm/FinalFormControls/DatePickerWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-duotone-svg-icons';
import { verify } from 'crypto';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '1rem'
    },
    remove: {
        color: theme.palette.error.main
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */



/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentReceivedModalProps {
    drugShipment: Dtos.DrugShipment | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    depotBatches: Dtos.DepotBatch[] | null;
    depotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
    onExit?: (node: HTMLElement) => void;
    pharmacyDrugs: Dtos.PharmacyDrug[] | null;
}

interface IDrugShipmentReceivedFormProps extends FormRenderProps<Dtos.DrugShipmentVerify> {
    drugShipmentVerify: Dtos.DrugShipmentVerify | null;
    depotBatches: Dtos.DepotBatch[] | null;
    depotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    pharmacyDrugs: Dtos.PharmacyDrug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IDrugShipmentReceivedForm extends Partial<Dtos.DrugShipmentVerify> {
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugUnitName = getDrugUnitName(true);

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const depotValidator = function (this: Yup.TestContext,
    value: number) {
    const form: IDrugShipmentReceivedForm = this.options.context as unknown as IDrugShipmentReceivedForm;
    const batchArray: Dtos.DrugShipmentDepotBatchVerify[] | undefined = form.drugShipmentDepotBatches;

    if (batchArray &&
        batchArray.length > 0 &&
        value) {
        if (batchArray
            .filter(b => b.depotBatchId === value)
            .length > 1) {
            return false;
        }
    }

    return true;
}

const validation = Yup.object().shape<IDrugShipmentReceivedForm>({
    consignmentNo: Yup.string(),
    drugShipmentDepotBatches: Yup.array<Dtos.DrugShipmentDepotBatchVerify>()
        .min(1, 'At least one batch of drugs must exist on the shipment')
        .of(Yup.object<Dtos.DrugShipmentDepotBatchVerify>().shape({
            depotBatchId: Yup.number().required()
                .test('only-one',
                    'You can only have one of each kind of batch',
                    depotValidator)
                .label('Batch'),
            drugId: Yup.number().required().label('Drug'),
            batchNo: Yup.string().required().label(`Batch No.`).when("depotBatchId", (depotBatchId: number, schema: Yup.NumberSchema) => { return depotBatchId === 0 ? schema.required() : schema.notRequired() }),
            batchExpiryDate: Yup.string().required().label(`Date of Expiration`).when("depotBatchId", (depotBatchId: number, schema: Yup.NumberSchema) => { return depotBatchId === 0 ? schema.required() : schema.notRequired() }),
            drugUnits: Yup.number().required().min(1).label(drugUnitName)
                .when(['drugUnitsReceived', 'drugUnitsLost', 'drugUnitsDamaged', 'drugUnitsDestroyed', 'drugUnitsQuarantined'],
                    (drugUnitsReceived: number, drugUnitsLost: number, drugUnitsDamaged: number, drugUnitsDestroyed: number, drugUnitsQuarantined: number, schema: Yup.NumberSchema) => {
                        const total: number = drugUnitsReceived + drugUnitsLost + drugUnitsDamaged + drugUnitsDestroyed + drugUnitsQuarantined;
                        return schema.oneOf([total], `${drugUnitName} total must be equal to the sum of the rest of the drug unit values`);
                    }),
            drugUnitsReceived: Yup.number().required().label(`${drugUnitName} received`).min(0),
            drugUnitsDamaged: Yup.number().required().label(`${drugUnitName} damaged`).min(0),
            drugUnitsDestroyed: Yup.number().required().label(`${drugUnitName} destroyed`).min(0),
            drugUnitsQuarantined: Yup.number().required().label(`${drugUnitName} quarantined`).min(0),
            drugUnitsLost: Yup.number().required().label(`${drugUnitName} lost`).min(0)
        }))
});




const DrugShipmentReceivedForm: React.FunctionComponent<IDrugShipmentReceivedFormProps> = ({
    drugShipmentVerify,
    depotBatches,
    depotBatchesLoadState,
    setModalOpen,
    onFormSave,
    pharmacyDrugs,
    ...formRenderProps
}) => {
    const form = useForm<Dtos.DrugShipmentVerify>();

    const classes = useStyles();

    const { enqueueSnackbar } = useSnackbar()

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);
    
    const depotBatchItems = React.useCallback((index: number) => {
        var drugShipmentDepotBatches = formRenderProps.form.getFieldState(`drugShipmentDepotBatches`)?.value ?? [];

        var drugShipmentDepotBatch = drugShipmentDepotBatches.length > 0 ? drugShipmentDepotBatches[index] : null;

        var drugId = drugShipmentDepotBatch?.drugId;

        if (!depotBatches || depotBatches.length === 0) {
            return [
                <MenuItem
                    key={0}
                    value={0}
                >
                    New Batch
                </MenuItem>
            ];
        }
        else {
            const availableDepotBatches = depotBatches
                .filter(db =>
                    db.available &&
                    (db.drugUnitsAllocated + db.drugUnitsDestroyed < db.drugUnitsTotal) &&
                    DateTime.local().startOf('day') < DateTime.fromISO(db.batch.allocationCutOffDate!)?.startOf('day'))
                .reduce((accumulator, currentValue) => {
                    const existingValue = accumulator[currentValue.batchId];

                    if (!existingValue ||
                        existingValue.depotBatchNo! > currentValue.depotBatchNo!) {
                        accumulator[currentValue.batchId] = currentValue;
                    }

                    return accumulator;
                }, {} as Record<number, Dtos.DepotBatch>);

            const depotBatchesForForm = Object.keys(availableDepotBatches).map(key => availableDepotBatches[key]) as Dtos.DepotBatch[];

            const depotBatchMenuItems =  depotBatches
                .filter(depotBatch => !drugId || depotBatch?.batch?.drugId === drugId)
                .map(depotBatch => {
                    return <MenuItem
                        key={depotBatch?.id}
                        value={depotBatch?.id}
                        disabled={depotBatchesForForm.find(x => x.id === depotBatch?.id) == null}
                    >
                        {`${depotBatch.batch.batchNo} (${depotBatch.depotBatchNo} Expires ${DateTime.fromISO(depotBatch.batch.dateExpiration!).toFormat('dd/MM/yyyy')})`}
                    </MenuItem>;
                });

            return [
                ...depotBatchMenuItems,
                <MenuItem
                    key={0}
                    value={0}
                >
                    New Batch
                </MenuItem>
            ]
        }
    }, [depotBatches, formRenderProps]);

    const drugItems = React.useMemo(() => {
        if (!pharmacyDrugs || pharmacyDrugs.length === 0) {
            return [];
        }
        else {
            const enabledDrugs = pharmacyDrugs?.filter(pd => pd.enabled)?.map(pd => pd.drug);

            return pharmacyDrugs.map(pd => {
                return <MenuItem
                    key={pd.drugId}
                    value={pd.drugId}
                    disabled={enabledDrugs?.find(x => x.id === pd.drugId) == null}
                >
                    {`${pd.drug.drugName}`}
                </MenuItem>;
            });
        }
    }, [pharmacyDrugs]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const drugShipmentDepotBatchHeader = (
        <React.Fragment>
            <Grid item xs={2}>
                Drug
            </Grid>
            <Grid item xs={3}>
                Batch
            </Grid>
            <Grid item xs>
                {
                    `Total ${drugUnitName} in Shipment`
                }
            </Grid>
            <Grid item xs>
                {
                    `${drugUnitName} in good condition`
                }
            </Grid>
            <Grid item xs>
                {`${drugUnitName} damaged *`}
            </Grid>
            <Grid item xs>
                {`${drugUnitName} destroyed *`}
            </Grid>
            <Grid item xs>
                {`${drugUnitName} quarantined *`}
            </Grid>
            <Grid item xs>
                {`${drugUnitName} lost`}
            </Grid>
            <Grid item zeroMinWidth>
                <Box style={{width: '64px'}}></Box>
            </Grid>
        </React.Fragment>
    );

    const onRemoveClick = React.useCallback((index: number) => {
        form.mutators.remove('drugShipmentDepotBatches', index);
    }, [form]);

    const drugShipmentDepotBatches = React.useMemo(() => {
        if (drugShipmentVerify &&
            drugShipmentVerify.drugShipmentDepotBatches &&
            drugShipmentVerify.drugShipmentDepotBatches.length > 0) {
            return <FieldArray name="drugShipmentDepotBatches">
                {({ fields }) => {
                    return fields.map((field, index) => {

                        return (
                            <React.Fragment key={index}>
                                <Grid container alignItems="flex-end" spacing={2}>
                                    <Grid item xs={2}>
                                        <Field
                                            name={`${field}.drugId`}
                                            component={SelectWrapper}
                                            formControlProps={{
                                                fullWidth: true
                                            }}
                                            disabled={formDisabled}
                                        >
                                            {drugItems}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={3}>
                                        {
                                            depotBatchesLoadState && depotBatchesLoadState.state && depotBatchesLoadState.state === RequestState.Pending ?
                                                <CircularProgress /> :
                                                <Field
                                                    name={`${field}.depotBatchId`}
                                                    component={SelectWrapper}
                                                    formControlProps={{
                                                        fullWidth: true
                                                    }}
                                                >
                                                    {depotBatchItems(index)}
                                                </Field>
                                        }
                                        <Field
                                            name={`${field}.batchNo`}
                                            component={TextFieldWrapper}
                                            fullWidth
                                            placeholder="Batch No."
                                            disabled={form?.getFieldState('drugShipmentDepotBatches')?.value ? form.getFieldState('drugShipmentDepotBatches')!.value![index].depotBatchId === 0 ? false : true : true}
                                        />
                                        <Field
                                            name={`${field}.batchExpiryDate`}
                                            component={DatePickerWrapper}
                                            formControlProps={{
                                                fullWidth: true
                                            }}
                                            placeholder="Date of Expiration"
                                            disabled={form?.getFieldState('drugShipmentDepotBatches')?.value ? form.getFieldState('drugShipmentDepotBatches')!.value![index].depotBatchId === 0 ? false : true : true}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnits`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnitsReceived`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnitsDamaged`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnitsDestroyed`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnitsQuarantined`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Field
                                            fullWidth
                                            name={`${field}.drugUnitsLost`}
                                            component={NumberTextField}
                                            disabled={formDisabled}
                                        />
                                    </Grid>
                                    <Grid item zeroMinWidth>
                                        <Button
                                            variant="text"
                                            color="default"
                                            className={classes.remove}
                                            onClick={() => onRemoveClick(index)}
                                            disabled={index === 0}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={faTimes} />
                                        </Button>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    });
                }}
            </FieldArray>;
        }

        return null;
    }, [drugShipmentVerify, depotBatchesLoadState, depotBatchItems, formDisabled, drugItems, classes, form, onRemoveClick]);

    const onAddClick = React.useCallback(() => {
        var drugShipmentDepotBatches = form.getFieldState("drugShipmentDepotBatches")?.value ?? [];

        var newDrugShipmentDepotBatch = {
            drugUnits: 0,
            drugUnitsReceived: 0,
            drugUnitsDamaged: 0,
            drugUnitsDestroyed: 0,
            drugUnitsQuarantined: 0,
            drugUnitsLost: 0
        } as Dtos.DrugShipmentDepotBatchVerify;

        form.change("drugShipmentDepotBatches", [...drugShipmentDepotBatches, newDrugShipmentDepotBatch]);
    }, [form])

    const [alertOpen, setOpen] = React.useState(false);
    
    const handleAlertOpen = async () => {
        try {
            const errors = formRenderProps.form.getState().errors;
            if (errors && Object.keys(errors).length > 0) {
                formRenderProps.form.submit();
                return;
            }
            setOpen(true);

        } catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the drug shipment: ${errorMessage}`, { variant: 'error' });
        }
    };

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            setOpen(false);
            if (await formRenderProps.handleSubmit()) {
                setModalOpen(false);
                if (onFormSave) {
                    onFormSave();
                }
            }
        } catch (error: any) {
            const errorMessage = error.message ?
            error.message :
            'Unknown error';
            enqueueSnackbar(`An error occurred while attempting to save the drug shipment: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, onFormSave, formRenderProps]);


  
    const handleClose = () => {
      setOpen(false);
    };

    return (
        <>
            <DialogContent>
                <Grid container alignItems="flex-end" spacing={2}>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="consignmentNo"
                            component={TextFieldWrapper}
                            type="text"
                            label={drugManagementSettings.consignmentNumberLabel}
                            disabled={true}
                        />
                    </Grid>
                </Grid>
                <Grid container alignItems="flex-end" spacing={2}>
                    {
                        drugShipmentDepotBatchHeader
                    }
                </Grid>
                {
                    drugShipmentDepotBatches
                }
                
                <Box className={classes.container}>
                    <div>
                        <Typography
                            variant="caption"
                        >
                            * requires BCT authorisation
                        </Typography>
                    </div>
                    <Button onClick={onAddClick}
                        color="primary"
                        variant="contained"
                        autoFocus
                        disabled={formDisabled}>
                        Add batch to shipment
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={handleAlertOpen}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
                <Dialog
                open={alertOpen}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                >
                    <DialogTitle id="recieved-alert" >
                        Notice
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            color='textPrimary'
                        >
                            Please ensure you have attached the pink Neo-N Drug label sticker to each received carton
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                    <Button autoFocus onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={onOkClick} color="primary">
                        Ok
                    </Button>
                    </DialogActions>
                </Dialog>
            </DialogActions>
        </>
    );
};

const DrugShipmentReceivedModal: React.FunctionComponent<IDrugShipmentReceivedModalProps> = (props) => {
    if (!props.drugShipment) {
        return null;
    }

    return (
        <InnerDrugShipmentReceivedModal {...props} />
    );
}


const InnerDrugShipmentReceivedModal: React.FunctionComponent<IDrugShipmentReceivedModalProps> = ({
    drugShipment,
    modalOpen,
    setModalOpen,
    depotBatches,
    depotBatchesLoadState,
    drugs,
    drugLoadState,
    onCloseClick,
    onExit,
    onFormSave,
    pharmacyDrugs
}) => {
    const drugShipmentVerify: Dtos.DrugShipmentVerify = Object.assign({},
        {
            id: drugShipment!.id,
            consignmentNo: drugShipment!.consignmentNo
        },
        {
            drugShipmentDepotBatches: Array.from(drugShipment!.drugShipmentDepotBatches, (drugShipmentDepotBatch) => {
                return Object.assign({}, { ...drugShipmentDepotBatch }, { drugUnitsReceived: 0}) as any as Dtos.DrugShipmentDepotBatchVerify;
            })
        });

    const drugShipmentReceivedForm = React.useCallback((props: FormRenderProps<Dtos.DrugShipmentVerify>) => {
        return (
            <DrugShipmentReceivedForm
                drugShipmentVerify={drugShipmentVerify}
                depotBatches={depotBatches}
                depotBatchesLoadState={depotBatchesLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                pharmacyDrugs={pharmacyDrugs}
                {...props}
            />
        );
    }, [setModalOpen, drugShipmentVerify, depotBatches, depotBatchesLoadState, drugLoadState, drugs, onFormSave, pharmacyDrugs]);

    const asyncSave = useAsyncFunction(DrugShipmentVerifyActions.verify, DrugShipmentVerifyActions.verifySuccess, DrugShipmentVerifyActions.verifyFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
                maxWidth="xl"
                onExit={onExit}
            >
                <DialogTitle id="alert-dialog-title">
                    Drug Shipment Received
                </DialogTitle>
                <AsyncForm
                    initialValues={drugShipmentVerify}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={drugShipmentReceivedForm}
                    mutators={{
                        ...arrayMutators
                    }}
                />
            </Dialog>
        </>
    );
}

export default DrugShipmentReceivedModal;